import { ChevronRight, History, Send, Settings, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ContactStatusBadge from '../components/ContactStatusBadge';
import PaymentHistory from '../components/payment-history/PaymentHistory';
import PortalHeader from '../components/PortalHeader';
import TransactionSettings from '../components/TransactionSettings';
import { userAPI } from '../services/api';
import { apiErrorDetail, responseData } from '../services/apiResponse';
import { clearAuthenticatedUser } from '../services/authStorage';

const maskedContactWallet = (contact) => {
  const address = contact.wallet_address || '';
  return address.length > 8 ? `xxxx${address.slice(-4)}` : address;
};

const Home = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = ['history', 'contacts', 'settings'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'history';
  const [user, setUser] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [transferringContactId, setTransferringContactId] = useState(null);
  const [transferError, setTransferError] = useState(null);
  const transferPending = useRef(false);

  useEffect(() => {
    let active = true;
    Promise.allSettled([userAPI.getCurrentUser(), userAPI.getContacts()]).then(([userResult, contactsResult]) => {
      if (!active) return;
      if (userResult.status === 'fulfilled') {
        setUser(responseData(userResult.value));
      } else if (userResult.reason?.response?.status === 401) {
        navigate('/login');
      }
      if (contactsResult.status === 'fulfilled') {
        setContacts(responseData(contactsResult.value)?.contacts || []);
      }
    });
    return () => { active = false; };
  }, [navigate]);

  const loadContacts = async () => {
    try {
      const response = await userAPI.getContacts();
      setContacts(responseData(response).contacts || []);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    }
  };

  const handleLogout = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await userAPI.logout(refreshToken);
      } catch (error) {
        console.error('Logout API failed:', error);
      }
    }
    clearAuthenticatedUser({ includeSession: true });
    navigate('/login');
  };

  const handleTransfer = async (contact) => {
    if (transferPending.current) return;
    transferPending.current = true;
    setTransferringContactId(contact.contact_id);
    setTransferError(null);
    try {
      const response = await userAPI.createContactTransfer(contact.contact_id);
      const requestId = responseData(response)?.request_id;
      if (!requestId) throw new Error('Missing transfer request');
      navigate(`/review/${encodeURIComponent(requestId)}`);
    } catch (error) {
      setTransferError(apiErrorDetail(error, 'Failed to start transfer. Please try again.'));
    } finally {
      transferPending.current = false;
      setTransferringContactId(null);
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const tabs = [
    { id: 'history', label: 'Payment Requests', mobileLabel: 'Requests', icon: History },
    { id: 'contacts', label: 'Contacts', mobileLabel: 'Contacts', icon: Users },
    { id: 'settings', label: 'Settings', mobileLabel: 'Settings', icon: Settings },
  ];

  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    next.delete('contact_id');
    if (tab === 'history') next.delete('tab');
    else next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <PortalHeader user={user} onLogout={handleLogout} />

      <main className="mx-auto max-w-7xl px-3 py-5 sm:px-4 sm:py-8">
        <div>
          <nav className="grid grid-cols-3 border-y border-slate-200 bg-white" aria-label="Account sections">
            {tabs.map(({ id, label, mobileLabel, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`flex min-h-14 items-center justify-center gap-1.5 border-b-2 px-2 text-xs font-medium transition-colors sm:gap-2 sm:px-6 sm:text-sm ${activeTab === id ? 'border-cyan-700 text-cyan-800' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800'}`}
              >
                <Icon className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
                <span className="hidden sm:inline">{label}</span>
                <span className="sm:hidden">{mobileLabel}</span>
              </button>
            ))}
          </nav>

          <div className="bg-white px-4 py-5 sm:px-6 sm:py-6">
            {activeTab === 'history' ? <PaymentHistory contact={contacts.find((contact) => contact.contact_id === searchParams.get('contact_id'))} /> : null}
            {activeTab === 'contacts' ? (
              <section aria-labelledby="saved-contacts-title">
                <div className="flex items-center justify-between">
                  <h1 id="saved-contacts-title" className="text-lg font-semibold text-slate-900">Saved Contacts</h1>
                  <button onClick={loadContacts} className="rounded px-2 py-1 text-xs font-semibold text-cyan-700 hover:bg-cyan-50">Refresh</button>
                </div>
                {transferError ? <p role="alert" className="mt-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{transferError}</p> : null}
                {contacts.length === 0 ? (
                  <div className="py-12 text-center">
                    <Users className="mx-auto h-10 w-10 text-slate-300" />
                    <p className="mt-3 text-sm font-medium text-slate-700">No saved contacts</p>
                    <p className="mt-1 text-xs text-slate-500">Contacts are saved when a payment request is created.</p>
                  </div>
                ) : (
                  <div className="mt-5 divide-y divide-slate-200 border-y border-slate-200">
                    {contacts.map((contact) => (
                      <div key={contact.contact_id} className="flex items-center justify-between gap-4 py-4">
                        <Link to={`/contacts/${encodeURIComponent(contact.contact_id)}`} className="group flex min-w-0 flex-1 items-center gap-3 rounded py-1 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h2 className="truncate text-sm font-semibold text-slate-900 group-hover:text-cyan-800">{contact.full_name}</h2>
                            <ContactStatusBadge status={contact.status} />
                          </div>
                          <p className="mt-1 text-xs text-slate-600">{contact.phone}</p>
                          {contact.wallet_address ? <p className="mt-1 font-mono text-xs text-slate-600">Wallet: {maskedContactWallet(contact)}</p> : null}
                          <p className="mt-1 text-xs text-slate-500">
                            {contact.transaction_count > 0 ? `${contact.transaction_count} transaction(s)${contact.last_transaction_date ? ` · Last ${formatDate(contact.last_transaction_date)}` : ''}` : 'No transfers yet'}
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleTransfer(contact)}
                          disabled={transferringContactId !== null}
                          aria-label={`Transfer to ${contact.full_name}${contact.wallet_address ? `, wallet ${maskedContactWallet(contact)}` : ''}`}
                          className="flex shrink-0 items-center gap-2 rounded bg-cyan-700 px-3 py-2 text-xs font-medium text-white hover:bg-cyan-800 disabled:cursor-wait disabled:opacity-50"
                        >
                          <Send className="h-4 w-4" />
                          {transferringContactId === contact.contact_id ? 'Starting…' : 'Transfer'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ) : null}
            {activeTab === 'settings' ? <TransactionSettings /> : null}
          </div>

          <p className="border-y border-slate-100 bg-white px-4 py-3 text-center text-[10px] text-slate-400">
            A service by Kivi3 Technologies Ltd · Licensed by Dubai International Financial Centre.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Home;
